import { sanitizeFilename } from '@/utils/sanitize-filename';
import React, { useCallback, useRef, useState } from 'react';
import { UploadCloudIcon } from '../../icons/upload-cloud';
import styles from './zip-dropzone.module.scss';

type ZipDropzoneProps = {
	disabled?: boolean;
	className?: string;
	onFileSelected?: (file: File) => void;
};

const VALID_ZIP_REGEX = /\.zip$/i;
const MAX_SIZE_BYTES = 50 * 1024 * 1024; // 50MB

export default function ZipDropzone(props: ZipDropzoneProps) {
	const [isDragging, setIsDragging] = useState(false);
	const [fileError, setFileError] = useState<{
		title: string;
		message: string;
	} | null>(null);

	const inputRef = useRef<HTMLInputElement | null>(null);

	const openFilePicker = useCallback(() => {
		if (!props.disabled) inputRef.current?.click();
	}, [props.disabled]);

	const validateAndSet = useCallback(
		(file?: File) => {
			if (!file) return;
			setFileError(null);

			const processedFile = new File(
				[file],
				sanitizeFilename(file.name),
				{
					type: file.type,
				},
			);

			const isZipMime =
				processedFile.type === 'application/zip' ||
				processedFile.type === 'application/x-zip-compressed';
			const isZipExt = /\.zip$/i.test(processedFile.name);

			if (!isZipMime && !isZipExt) {
				setFileError({
					title: 'No podemos cargar este archivo',
					message: 'Solo se aceptan archivos .ZIP',
				});
				return;
			}

			if (!VALID_ZIP_REGEX.test(processedFile.name)) {
				setFileError({
					title: 'No podemos cargar este archivo',
					message: 'El nombre del archivo es inválido',
				});
				return;
			}

			if (processedFile.size > MAX_SIZE_BYTES) {
				setFileError({
					title: 'No podemos cargar este archivo',
					message: 'El archivo pesa más de 50MB',
				});
				return;
			}

			setFileError(null);
			props.onFileSelected?.(processedFile);
		},
		[props.onFileSelected],
	);

	const onInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			validateAndSet(file);
			e.target.value = '';
		},
		[validateAndSet],
	);

	const prevent = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const onDragEnter = (e: React.DragEvent) => {
		prevent(e);
		if (!props.disabled) setIsDragging(true);
	};

	const onDragOver = (e: React.DragEvent) => {
		prevent(e);
		if (!props.disabled) setIsDragging(true);
	};

	const onDragLeave = (e: React.DragEvent) => {
		prevent(e);
		setIsDragging(false);
	};

	const onDrop = (e: React.DragEvent) => {
		prevent(e);
		setIsDragging(false);
		if (props.disabled) return;
		const file = e.dataTransfer.files?.[0];
		validateAndSet(file);
	};

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (props.disabled) return;
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			openFilePicker();
		}
	};

	return (
		<div
			className={`${styles['zip-dropzone-wrapper']} ${props.className ?? ''}`}
		>
			<div
				role="button"
				tabIndex={props.disabled ? -1 : 0}
				aria-disabled={props.disabled}
				aria-label="ZIP Upload"
				onClick={openFilePicker}
				onKeyDown={onKeyDown}
				onDragEnter={onDragEnter}
				onDragOver={onDragOver}
				onDragLeave={onDragLeave}
				onDrop={onDrop}
				className={[
					styles['zip-dropzone'],
					isDragging ? styles['is-dragging'] : '',
					fileError ? styles['error'] : '',
				].join(' ')}
			>
				<UploadCloudIcon />
				<div className={styles['text']}>
					<div className={styles['label']}>Sube tu archivo ZIP</div>
					<div className={styles['hint']}>
						Haz clic o arrastra y suelta para subir
					</div>
				</div>

				{fileError && (
					<div className={styles['error-message']}>
						<div className={styles['title']}>{fileError.title}</div>
						<div className={styles['message']}>
							{fileError.message}
						</div>
					</div>
				)}

				<input
					ref={inputRef}
					type="file"
					accept=".zip,application/zip,application/x-zip-compressed"
					onChange={onInputChange}
					style={{
						position: 'absolute',
						width: 1,
						height: 1,
						opacity: 0,
					}}
					tabIndex={-1}
					disabled={props.disabled}
				/>
			</div>
		</div>
	);
}
